import { createPreviewForm } from '@/lib/api/formApi'
import {
  CreatePreviewFormMutationVariables,
  DescribableItemMode,
  FieldClass,
  FieldRuleMode,
  GoodsItem,
  LikertChoiceStyle,
  ReservationQuotaSettingType,
  Scene,
  type FormSettingAttributes,
} from '@/typings/domain'
import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { keyBy, last } from 'lodash-es'
import type { RootState } from '../store'
import { selectConversation } from './aiChatSlice'

export type IdentifiedScene = Scene | 'unknown'

export const supportedScenes: Scene[] = [
  Scene.Form,
  Scene.Survey,
  Scene.Registry,
  Scene.OnlinePayment,
  Scene.Reservation,
  Scene.Exam,
  Scene.Evaluation,
]
export type ShortenWeekName = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun'

export type GeneratedFormArtifactType = {
  scene?: Scene
  name: string
  description: string
  fields: (AIGeneratedField & { _id?: string })[]
  theme?: AIGeneratedFormHeaderImage
  field_rules?: Maybe<AIGeneratedFieldRule[]>
  field_rules_outline?: Maybe<string>
  alternative_header_image_urls?: string[]
  setting?: FormSettingAttributes
}

export type AIGeneratedFieldExamSetting = {
  score?: Maybe<number>
  answer?: Maybe<string[]>
  explanation?: Maybe<string>
}

export type AIGeneratedFieldChoice = {
  score?: Maybe<number>
  value?: string
  name: string
  type?: 'other'
  quotaSetting?: number
  descriptionMode?: DescribableItemMode
  descriptionText?: string
  descriptionUrl?: string
  subChoices?: AIGeneratedFieldChoice[]
}

export type AIGeneratedField = {
  apiCode?: string
  type: FieldClass
  customizedType?: string
  label: string
  notes?: string
  required?: boolean
  choices?: AIGeneratedFieldChoice[]
  levels?: number
  likertChoiceStyle?: LikertChoiceStyle
  statements?: string[]
  dimensions?:
    | {
        maximumRatingsDisplayText?: string
        minimumRatingsDisplayText?: string
      }
    | {
        label: string
        notes?: string
        type: FieldClass
        required?: boolean
        choices?: AIGeneratedFieldChoice[]
      }[]
  goodsItems?: Pick<GoodsItem, 'name' | 'price' | 'dimensions' | 'skus' | 'layout'>[]
  reservationItems?: {
    name: string
    description?: string
    startDate?: string
    endDate?: string
    quotaSetting?: {
      type?: ReservationQuotaSettingType
      availableDaysOfWeek?: ShortenWeekName[]
      dailyTimeRangeQuotas?: {
        start?: string // HH:mm
        end?: string // HH:mm
        forWday?: ShortenWeekName
        quota?: Maybe<number>
      }[]
    }
  }[]
  latitude?: string
  longitude?: string
  navAddress?: string
  enableAutoPosition?: boolean

  examSetting?: AIGeneratedFieldExamSetting
}

export type AIGeneratedFieldRule = {
  trigger_field: string
  trigger_choices: Maybe<string[]>
  targets: string[]
  targets_display_mode: FieldRuleMode
}

export type AIGeneratedFormHeaderImage = {
  headerImageId?: string
  headerImageUrl: string
  headerImageAvgColor?: string
}

export type AIGeneratedForm = {
  id: string
  conversationId: string
  messageId?: string

  name?: string
  scene?: Scene
  theme?: AIGeneratedFormHeaderImage
  description?: string
  fields?: (AIGeneratedField & { _id: string })[]
  fieldRulesOutline?: Maybe<string>
  fieldRules?: Maybe<AIGeneratedFieldRule[]>
  setting?: FormSettingAttributes

  alternativeHeaderImageUrls?: string[]

  done: boolean
  interrupted?: boolean
  isNotModifiedDuringMessage: boolean

  updatingState: {
    createdByUpdate: boolean
    oldValues?: { name: string; description: string }
    changingProperties: ('headerImage' | 'name' | 'description' | 'fields' | 'fieldRules')[]
  }
}

const generatedFormAdapter = createEntityAdapter<AIGeneratedForm>()

export type ConversationMarks = {
  messageMarks: Record<string, ConversationMessageMarks>
}

export type ConversationMessageMarks = {
  identifiedUnsupportedScene?: Scene
}

export type AIGeneratedFormPreviewState = {
  cacheToken: string
  expiresAt: number
  cachePayload: string
}

type FormAIGenerationState = {
  conversationMarks: Record<string, ConversationMarks>
  headerImageEditResult: Record<string, string>
  generatedFormIdsOfConversation: Record<string, string[]>
  generatedFormPreviewStates: Record<string, AIGeneratedFormPreviewState>

  navigatedAway?: boolean
}

const formAIGenerationSlice = createSlice({
  name: 'formAIGeneration',
  initialState: generatedFormAdapter.getInitialState<FormAIGenerationState>({
    conversationMarks: {},
    headerImageEditResult: {},
    generatedFormIdsOfConversation: {},
    generatedFormPreviewStates: {},
  }),
  reducers: {
    setConversationMessageMarks: (
      state,
      action: PayloadAction<{ conversationId: string; messageId: string; marks: ConversationMessageMarks }>
    ) => {
      const { conversationId, messageId, marks } = action.payload
      const conversationMessageMarks = getOrInitConversationMessageMarks(state, conversationId, messageId)
      if (marks.identifiedUnsupportedScene !== undefined) {
        conversationMessageMarks.identifiedUnsupportedScene = marks.identifiedUnsupportedScene
      }
    },
    upsertGeneratedForm: (state, { payload }: PayloadAction<AIGeneratedForm>) => {
      let generatedForm = state.entities[payload.id]
      if (!generatedForm) {
        generatedForm = { ...payload }
        generatedFormAdapter.addOne(state, generatedForm)
        getOrInitGeneratedFormIdsOfConversation(state, generatedForm.conversationId).push(generatedForm.id)
      }

      generatedForm.name = payload.name
      generatedForm.description = payload.description
      generatedForm.fields = [...(payload.fields ?? [])]
      generatedForm.scene = payload.scene
      generatedForm.theme = payload.theme
      generatedForm.done = payload.done
      generatedForm.messageId = payload.messageId
      generatedForm.alternativeHeaderImageUrls = payload.alternativeHeaderImageUrls
      generatedForm.isNotModifiedDuringMessage = payload.isNotModifiedDuringMessage
      generatedForm.fieldRules = payload.fieldRules
      generatedForm.fieldRulesOutline = payload.fieldRulesOutline
      generatedForm.setting = payload.setting
      if (typeof payload.interrupted === 'boolean') generatedForm.interrupted = payload.interrupted
      generatedForm.updatingState = { ...payload.updatingState }
    },
    removeGeneratedForm: (state, { payload }: PayloadAction<{ formId: string }>) => {
      const conversationId = state.entities[payload.formId]?.conversationId
      generatedFormAdapter.removeOne(state, payload.formId)
      if (conversationId) {
        const generatedFormIds = getOrInitGeneratedFormIdsOfConversation(state, conversationId)
        const index = generatedFormIds.indexOf(payload.formId)
        if (index !== -1) generatedFormIds.splice(index, 1)
      }
      delete state.generatedFormPreviewStates[payload.formId]
    },
    updateGeneratedFormHeaderImage: (
      state,
      { payload }: PayloadAction<{ formId: string; headerImage: AIGeneratedFormHeaderImage }>
    ) => {
      const form = state.entities[payload.formId]
      if (!form) return
      form.theme = { ...form.theme, ...payload.headerImage }
    },
    setHeaderImageEditResult: (state, { payload }: PayloadAction<Record<string, string>>) => {
      state.headerImageEditResult = payload
    },
    resetStateOfConversation: (state, { payload }: PayloadAction<{ conversationId: string }>) => {
      const generatedFormIds = selectValidGeneratedFormsByConversationId(state, payload.conversationId).map(
        form => form.id
      )
      if (generatedFormIds.length) generatedFormAdapter.removeMany(state, generatedFormIds)
      delete state.conversationMarks[payload.conversationId]
      for (const id of generatedFormIds) {
        delete state.generatedFormPreviewStates[id]
      }
    },
    setGeneratedFormPreviewState: (
      state,
      { payload }: PayloadAction<{ formId: string; state: AIGeneratedFormPreviewState }>
    ) => {
      state.generatedFormPreviewStates[payload.formId] = payload.state
    },
    setNavigatedAway: (state, { payload }: PayloadAction<boolean>) => {
      state.navigatedAway = payload
    },
  },
  extraReducers: builder => {
    builder.addCase(createPreviewFormThunk.fulfilled, (state, action) => {
      const generatedFormId = action.meta.arg.generatedFormId
      const cacheToken = action.payload
      state.generatedFormPreviewStates[generatedFormId] = {
        cacheToken,
        expiresAt: Date.now() + 20 * 60 * 1000, // 20 mins
        cachePayload: action.meta.arg.cachePayload,
      }
    })
  },
})

export const createPreviewFormThunk = createAsyncThunk<
  string,
  CreatePreviewFormMutationVariables['input'] & { generatedFormId: string; cachePayload: string }
>('forms/createPreview', async ({ generatedFormId, cachePayload, ...variables }, thunkAPI) => {
  const { data } = await createPreviewForm(variables)
  return data?.previewForm?.cacheToken ?? thunkAPI.rejectWithValue({ message: 'Failed to create preview form!' })
})

export const selectGeneratedFormById = (state: RootState, id: string) => state.formAIGeneration.entities[id]

export const selectLatestGeneratedForm = createSelector(
  (state: RootState) => state.formAIGeneration.entities,
  (state: RootState, conversationId: string) =>
    last(state.formAIGeneration.generatedFormIdsOfConversation[conversationId]),
  (entities, lastGeneratedFormId) => (lastGeneratedFormId ? entities[lastGeneratedFormId] : undefined)
)

export const selectValidGeneratedFormsByConversationId = createSelector(
  (state: RootState['formAIGeneration']) => state.entities,
  (state: RootState['formAIGeneration']) => state.ids,
  (_state: RootState['formAIGeneration'], conversationId: string) => conversationId,
  (entities, ids, conversationId) =>
    ids
      .map(id => entities[id])
      .filter(form => form?.conversationId === conversationId && !form.isNotModifiedDuringMessage) as AIGeneratedForm[]
)

export const selectGeneratedFormDataByConversationId = createSelector(
  (state: RootState, conversationId: string) =>
    selectValidGeneratedFormsByConversationId(state.formAIGeneration, conversationId),
  (state: RootState, conversationId: string) => selectConversation(state.aiChat, conversationId),
  (state: RootState) => state.formAIGeneration.generatedFormPreviewStates,
  (state: RootState) => state.formAIGeneration.headerImageEditResult,
  (generatedForms, conversation, previewStates, headerImageEditResult) => {
    const conversationMessages = keyBy(conversation?.messages ?? [], 'id')
    return generatedForms.map(form => ({
      form: {
        ...form,
        theme: form.theme
          ? {
              ...form.theme,
              headerImageUrl: form.theme.headerImageUrl
                ? (headerImageEditResult[form.theme.headerImageUrl] ?? form.theme.headerImageUrl)
                : undefined,
            }
          : undefined,
      } as AIGeneratedForm,
      previewStates: previewStates[form.id],
      backendMessageId: form.messageId ? conversationMessages[form.messageId]?.backendMessageId : undefined,
    }))
  }
)

function getOrInitConversationMarks(state: RootState['formAIGeneration'], conversationId: string) {
  let conversationMarks = state.conversationMarks[conversationId]
  if (!conversationMarks) {
    conversationMarks = { messageMarks: {} }
    state.conversationMarks[conversationId] = conversationMarks
  }
  return conversationMarks
}

function getOrInitConversationMessageMarks(
  state: RootState['formAIGeneration'],
  conversationId: string,
  messageId: string
) {
  const messageMarksState = getOrInitConversationMarks(state, conversationId).messageMarks
  let conversationMessageMarks = messageMarksState[messageId]
  if (!conversationMessageMarks) {
    conversationMessageMarks = {}
    messageMarksState[messageId] = conversationMessageMarks
  }
  return conversationMessageMarks
}

function getOrInitGeneratedFormIdsOfConversation(state: RootState['formAIGeneration'], conversationId: string) {
  let generatedFormIds = state.generatedFormIdsOfConversation[conversationId]
  if (!generatedFormIds) {
    generatedFormIds = []
    state.generatedFormIdsOfConversation[conversationId] = generatedFormIds
  }
  return generatedFormIds
}

export const {
  setConversationMessageMarks,
  upsertGeneratedForm,
  removeGeneratedForm,
  updateGeneratedFormHeaderImage,
  setHeaderImageEditResult,
  resetStateOfConversation,
  setNavigatedAway,
} = formAIGenerationSlice.actions

export default formAIGenerationSlice.reducer
